<template>
  <v-container>
    <h2>點數設定 | {{ merchantName }}</h2>
    <br/><br/>
    <v-row align="center" no-gutters>
      <v-col cols="12" md="6">
        <span class="text-h3" style="vertical-align: middle;">店家於後台可設定的點數</span>
        <br/>
        <span class="text--disabled"> 開啓後，店家可於店家後台看到此點數並進行開通使用。</span>
      </v-col>
      <v-col>
        <v-form @submit.native.prevent>
          <v-text-field dense outlined v-model="form.search.usagePointKeyword"
              prepend-inner-icon="mdi-magnify"
              :disabled="isProgressing || isInitializing"
              placeholder="(點數名稱關鍵字)" clearable>
          </v-text-field>
        </v-form>
      </v-col>
    </v-row>
    <v-divider class="mb-2"></v-divider>
    <template v-if="!filteredUsagePoints.length">
      <div class="center-container">
        <div v-if="isInitializing" class="text-center mt-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else class="content">
          沒有可設定的點數
        </div>
      </div>
    </template>
    <v-list>
      <v-list-item
        v-for="(item, index) in filteredUsagePoints"
        :key="index"
      >
        <v-list-item-content>
          <v-list-item-title> {{ item.name }} </v-list-item-title>
        </v-list-item-content>

        <v-list-item-action>
          <v-switch inset 
            v-model="item.enabled"
            :disabled="isProgressing" 
            :loading="loadingUsageKey === item.code"
            @change="() => togglePointStatus(item, 'usage')"
          ></v-switch>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <br/><br/>
    <v-row align="center" no-gutters>
      <v-col cols="12" md="6">
        <span class="text-h3" style="vertical-align: middle;">點數開通狀態</span>
        <br/>
        <span class="text--disabled"> 僅支援協助店家關閉點數使用，開通需由店家於店家後台自行操作。</span>
      </v-col>
      <v-col>
        <v-form @submit.native.prevent>
          <v-text-field dense outlined v-model="form.search.keyword"
              prepend-inner-icon="mdi-magnify"
              :disabled="isProgressing || isInitializing"
              placeholder="(點數名稱關鍵字)" clearable>
          </v-text-field>
        </v-form>
      </v-col>
    </v-row>
    
    <v-divider class="mb-2"></v-divider>

    
    <template v-if="!filteredPoints.length">
      <div class="center-container">
        <div v-if="isInitializing" class="text-center mt-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else class="content">
          沒有可設定的點數
        </div>
      </div>
    </template>
    <v-list>
      <v-list-item
        v-for="(item, index) in filteredPoints"
        :key="index"
      >
        <v-list-item-content>
          <v-list-item-title> {{ item.name }} </v-list-item-title>
        </v-list-item-content>

        <v-list-item-action>
          <v-switch inset 
            v-model="item.enabled"
            :disabled="isProgressing || !item.enabled" 
            :loading="loadingKey === item.code"
            @change="() => togglePointStatus(item, 'enabled')"
          ></v-switch>
        </v-list-item-action>
      </v-list-item>
    </v-list>
  </v-container>
</template>

<script>
// 將 import 語句更改為 require 語句，以解決 "Cannot use import statement outside a module" 錯誤
const axios = require('axios');

export default {
  name: 'MerchantPointSettings',
  props: {
    merchantId: String,
    merchantName: String,
    merchant: Object
  },

  data() {
    return {
      form: {
        search: {
          keyword: '',
          usagePointKeyword: ''
        }
      },
      rawPoints: [], // 所有點數的原始清單
      enabledPoints: [], // 店家後台已開通的點數清單 (由 fetchMerchantPoint 獲取)
      usagePoints: [], // 店家於 DDPC 可設定的點數清單 (由 fetchStoreUsagePoints 獲取)

      points: [], // 用於顯示在「點數開通狀態」區塊
      usages: [], // 用於顯示在「店家於後台可設定的點數」區塊

      loadingKey: undefined, // 用於「店家於後台可設定的點數」的 loading 狀態
      loadingUsageKey: undefined, // 用於「點數開通狀態」的 loading 狀態

      isInitializing: false, // 判斷是否正在初始化中
      isProgressing: false, // 判斷是否有任何切換操作正在進行中
    };
  },

  computed: {
    user() {
      return this.$store.getters.user;
    },
    organizations() {
      return this.$store.getters.organizations.map(org => {
        return {
          text: org['name'],
          value: org['id']
        };
      });
    },
    filteredPoints () {
      if (this.form.search.keyword && this.form.search.keyword.trim()) {
        return this.points.filter(item =>
          item.name.toLowerCase().includes(this.form.search.keyword.trim().toLowerCase())
        );
      } else {
       return this.points;
      }
    },
    filteredUsagePoints () {
      if (this.form.search.usagePointKeyword && this.form.search.usagePointKeyword.trim()) {
        return this.usages.filter(item =>
          item.name.toLowerCase().includes(this.form.search.usagePointKeyword.trim().toLowerCase())
        );
      } else {
       return this.usages;
      }
    },
  },

  watch: {
    'merchantId': {
      immediate: true,
      handler(mId) {
        if (mId) { // 確保 merchantId 存在
          this.init();
        }
      }
    }
  },

  methods: {
    /**
     * 規範化 API 返回的點數數據。
     * @param {Array} items - 原始點數列表。
     * @returns {Array} 規範化後的點數列表。
     */
    normalizeData(items) {
      if (!items) return [];
      return items.map(item => ({
        name: item.pointName,
        id: item.pointId,
        code: item.pointCode
      }));
    },

    /**
     * 初始化：獲取所有點數、店家後台點數及店家使用點數。
     */
    async init() {
      this.isInitializing = true;
      try {
        // 並行獲取所有必要的點數數據
        await Promise.all([
          this.fetchPoints(), // 獲取所有通用點數
          this.fetchMerchantPoint(), // 獲取店家後台已設定點數
          this.fetchStoreUsagePoints() // 獲取店家於 DDPC 可使用點數
        ]);
        
        // 根據獲取到的數據重置顯示列表
        this.resetDisplayedPoints();
        this.resetDisplayedUsagePoints();

      } catch (err) {
        console.error("初始化點數設定失敗:", err);
        // 錯誤通知已在各自的 fetch 方法中處理
      } finally {
        this.isInitializing = false;
      }
    }, 

    /**
     * 獲取所有通用點數列表。
     */
    async fetchPoints() {
      try {
        const url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/core/points-usages?offset=999999`;
        const response = await axios.get(url);
        this.rawPoints = this.normalizeData(response.data.data);
      } catch (err) {
        console.error(`獲取通用點數清單失敗`, err);
        this.$notify({
          title: '無法取得DDPAY可以使用的點數清單',
          text: err.response?.data?.message || '',
          type: 'error'
        });
        throw err; // 向上拋出，讓 init 中的 Promise.all 知道失敗
      }
    },

    /**
     * 獲取店家後台已開通的點數清單。
     */
    async fetchMerchantPoint() {
      try {
        const url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/core/merchants/${this.merchantId}/points`;
        const response = await axios.get(url);
        // 注意：enabledPoints 結構與 rawPoints 不同，所以不需要 normalizeData
        this.enabledPoints = response.data; 
      } catch (err) {
        console.error(`獲取店家支援點數清單失敗`, err);
        this.$notify({
          title: '無法取得店家支援的點數清單',
          text: err.response?.data?.message || '',
          type: 'error'
        });
        throw err;
      }
    },

    /**
     * 獲取店家於 DDPC 可設定的點數清單。
     */
    async fetchStoreUsagePoints() {
      try {
        const url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/core/merchant/${this.merchantId}/store-points-usages`;
        const response = await axios.get(url);
        // 注意：usagePoints 結構與 rawPoints 不同，所以不需要 normalizeData
        this.usagePoints = this.normalizeData(response.data.data); 
      } catch (err) {
        console.error(`獲取店家可以使用的點數清單失敗`, err);
        this.$notify({
          title: '無法取得店家可以使用的點數清單',
          text: err.response?.data?.message || '',
          type: 'error'
        });
        throw err;
      }
    },

    /**
     * 根據 rawPoints 和 usagePoints 重置顯示在「點數開通狀態」區塊的列表。
     */
    resetDisplayedPoints () {
      const enabledCodes = new Set(this.enabledPoints.map(p => p.code));
      this.points = this.rawPoints.map(point => ({
        ...point,
        enabled: enabledCodes.has(point.code)
      })).sort((a, b) => a.code.localeCompare(b.code));
    },

    
    /**
     * 根據 rawPoints 和 enabledPoints 重置顯示在「店家於後台可設定的點數」區塊的列表。
     */
    resetDisplayedUsagePoints () {
      const usageCodes = new Set(this.usagePoints.map(p => p.code));
      this.usages = this.rawPoints.map(point => ({
        ...point,
        enabled: usageCodes.has(point.code)
      })).sort((a, b) => a.code.localeCompare(b.code));
    },

    /**
     * 檢查點數是否已啟用。
     * @param {string} code - 點數代碼。
     * @param {string} type - 'enabled' 或 'usage'。
     *    usage:店家可於DDPC設定的點數
     *    enabled:店家已開通的點數
     * @returns {boolean}
     */
    isPointEnabled(code, type) {
      if (type === 'enabled') {
        return this.enabledPoints.some(e => e.code === code);
      } else if (type === 'usage') {
        return this.usagePoints.some(e => e.code === code);
      }
      return false;
    },

    /**
     * 切換點數的啟用/禁用狀態。
     * @param {Object} item - 點數項目。
     * @param {string} type - 'enabled' 或 'usage'。
     */
    async togglePointStatus(item, type) {
      if (this.isProgressing) return; // 防止重複點擊
      this.isProgressing = true;

      const previousStatus = this.isPointEnabled(item.code, type);
      const newStatus = !previousStatus;
      const targetList = (type === 'enabled' ? this.points : this.usages);
      const targetItemInList = targetList.find(p => p.code === item.code);
     
      // 記錄 loading 狀態
      if (type === 'enabled') {
        this.loadingKey = item.code;
      } else {
        this.loadingUsageKey = item.code;
      }

      try {
        // 呼叫 API 更新狀態
        await this.updatePointStatusApi(item, newStatus, type);

        // 成功後更新本地狀態
        const currentTrackingList = (type === 'enabled' ? this.enabledPoints : this.usagePoints);
        if (newStatus) {
          currentTrackingList.push({ name: item.name, code: item.code, id: item.id });
        } else {
          const index = currentTrackingList.findIndex(p => p.code === item.code);
          if (index !== -1) {
            currentTrackingList.splice(index, 1);
          }
        }
        
        // 重新計算顯示列表的 enabled 狀態
        if (type === 'enabled') {
          this.resetDisplayedPoints();
        } else {
          this.resetDisplayedUsagePoints();
        }

      } catch (err) {
        // API 失敗時還原前端顯示狀態 + 顯示錯誤
        console.error(`Failed to update ${type} point settings for ${item.name}:`, err);
        if (targetItemInList) targetItemInList.enabled = previousStatus; // 恢復顯示狀態
      } finally {
        // 清除 loading 狀態
        if (type === 'enabled') {
          this.loadingKey = null;
        } else {
          this.loadingUsageKey = null;
        }
        this.isProgressing = false;
      }
    },

    /**
     * 呼叫後端 API 更新點數狀態。
     * @param {Object} point - 點數項目。
     * @param {boolean} status - 新的狀態 (true 為開通/新增, false 為關閉/刪除)。
     * @param {string} type - 'enabled' 或 'usage' 。
     */
    async updatePointStatusApi(point, status, type) {
      let url = '';
      let message = '';
      let payload = {};

      if (type === 'enabled') {
        url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/core/merchants/${this.merchantId}/points`;
        payload = { pointCodes: [point.code] };
        message = status ? `已開通店家使用點數 ${point.name}` : `已關閉店家使用點數 ${point.name}`;
      } else if (type === 'usage') {
        url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/core/points-usages`;
        payload = {
          action: status ? "create" : "delete",
          type: "merchants",
          pointId: point.id, // 使用動態 point.id
          ids: [this.merchantId] // 通常這裡會是 merchantId
        };
        message = status ? `已開通店家使用後台點數 ${point.name}` : `已關閉店家後台點數 ${point.name}`;
      } else {
        throw new Error('不支援的點數類型');
      }

      try {
        if (!status) { // 關閉操作需要確認
          const result = await this.$swal({
            text: type === 'enabled' ? `是否確認要關閉店家 ${point.name} 使用？` : `是否確認要關閉店家後台點數 ${point.name}？`,
            icon: 'warning',
            confirmButtonText: '確認',
            cancelButtonText: '取消',
            showCancelButton: true,
            showCloseButton: true
          });
          if (!result.isConfirmed) {
            throw new Error('Cancel operation');
          }
        }
        
        // 根據狀態和類型選擇請求方法
        if (type === 'enabled') {
          if (status) {
            await axios.post(url, payload);
          } else {
            await axios.delete(url, { data: payload });
          }
        } else if (type === 'usage') {
          // usage 點數的開關都是用 POST 請求，但 action 欄位不同
          await axios.post(url, payload);
        }

        this.$notify({ title: message, type: 'success' });
      } catch (err) {
        if (err.message !== 'Cancel operation') {
          this.$notify({
            title: `無法設定點數狀態 - ${point.name}`,
            text: err.response?.data?.message || '',
            type: 'error'
          });
        }
        throw err; // 拋出錯誤，讓 togglePointStatus 捕獲並還原狀態
      }
    },
  },

  mounted() {
    // 首次載入時會由 watch 觸發 init()
  }
}
</script>

<style scoped>
/* 保持原始樣式 */
img.disabled-filter {
  filter: grayscale(100%);
}

.list-item-border {
  border-bottom: 1px solid #e0e0e0;
}

.list-item-border:last-child {
  border-bottom: none;
}

.center-container {
  display: grid;
  place-items: center;
  height: 100px;
}

.content {
  font-size: 1.5em;
}
</style>
